import React, { useState } from 'react';
import StatsPieChart from '../charts/StatsPieChart';
import StatsBarChart from '../charts/StatsBarChart';
import StatBlock from '../StatBlock';
import { toStatItems, asHectares } from '../statBlockUtils';
import type { YearStats, StatsChartData, RegionStats } from '../statsTypes';
import { resolveRegionStats, canonicalRegionLabel } from '../regionResolver';

const REGIONS = ['NT Total', 'Beaufort Delta', 'Sahtu', 'South Slave', 'North Slave', 'Deh Cho'];

interface TenYearAverageTabProps {
  data: YearStats | null;
}

function getRegionStats(data: YearStats, regionName: string): RegionStats | null {
  if (regionName === 'NT Total') return data.totals;
  return resolveRegionStats(data.regions, regionName);
}

const TenYearAverageTab: React.FC<TenYearAverageTabProps> = ({ data }) => {
  const [activeRegion, setActiveRegion] = useState('NT Total');

  if (!data) return <p>No data available</p>;

  const stats = getRegionStats(data, activeRegion);

  const causeData: StatsChartData[] = stats
    ? [
        { name: 'Natural', value: stats.natural_causes, color: '#2E7D32' },
        { name: 'Human', value: stats.human_causes, color: '#C62828' },
      ]
    : [];

  const fireComparison: StatsChartData[] = Object.entries(data.regions).map(([name, s]) => ({
    name: canonicalRegionLabel(name),
    value: s.total_fires,
  }));

  const areaComparison: StatsChartData[] = Object.entries(data.regions).map(([name, s]) => ({
    name: canonicalRegionLabel(name),
    value: s.total_area,
  }));

  return (
    <div className="stats-year-tab">
      <div className="stats-sub-tabs">
        {REGIONS.map(region => (
          <button
            key={region}
            className={`stats-sub-tab ${activeRegion === region ? 'stats-sub-tab--active' : ''}`}
            onClick={() => setActiveRegion(region)}
          >
            {region}
          </button>
        ))}
      </div>
      {stats && (
        <>
        <div className="stats-blocks">
          <StatBlock title="Totals" items={[
            { label: 'Fire Frequency', value: stats.total_fires },
            { label: 'Hectares Burned', value: asHectares(stats.total_area) },
          ]} />
          <StatBlock title="Cause" items={toStatItems(causeData)} />
          <StatBlock title="Regional Fire Count" items={toStatItems(fireComparison)} />
          <StatBlock title="Regional Hectares" items={toStatItems(areaComparison, asHectares)} />
        </div>
        <div className="stats-charts-grid">
          <StatsPieChart data={causeData} title="Cause Breakdown (10Y Avg)" />
          <StatsBarChart data={fireComparison} title="Regional Fire Comparison (10Y Avg)" color="#1976D2" />
          <StatsBarChart data={areaComparison} title="Regional Area Comparison (10Y Avg)" color="#F57C00" />
        </div>
        </>
      )}
    </div>
  );
};

export default TenYearAverageTab;
